import { Button } from '@praktio/common-ui';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { RootState } from '../../../ducks/store';
import { getTeams } from '../../../ducks/teams';
import { buildTeamHierarchy } from '../../../utils/functions';

import './TeamsTree.scss';

const TeamsTree = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const teamId = params.teamId as string;
  const { teamsHierarchy, teamById } = useSelector((state: RootState) => {
    const teams = state.teams.list || [];
    const teamById: IKey = {};
    teams.forEach((t) => (teamById[t._id] = t));

    const teamsHierarchy = buildTeamHierarchy({ teams: state.teams.list || [], sorted: true });
    return {
      teamsHierarchy,
      teamById,
    };
  });

  const [expandedNodes, setExpandedNodes] = useState<IKey>({});

  useEffect(() => {
    dispatch(getTeams());
  }, [dispatch]);

  let allExpanded = true;
  let queue =
    teamsHierarchy?.map((t) => {
      return cloneDeep(t);
    }) || [];

  let item = queue.shift();
  const depthById: IKey = {};
  let orderedNodes: any = [];
  const collapsedParents: IKey = {};
  while (item) {
    const { _id, children, parent } = item;

    // Do null coalescing (?? -1) here because if the
    // parent isn't found, it should be top level (0)
    const depth = parent ? (depthById[parent] ?? -1) + 1 : 0;
    depthById[_id] = depth;

    // Render the node if it has no parent/parent doesn't exist, or it's parent is expanded
    if (!parent || !teamById[parent] || (expandedNodes[parent] && !collapsedParents[parent])) {
      orderedNodes.push({ ...item, depth });
    } else {
      collapsedParents[_id] = true;
    }

    if (children) {
      queue.unshift(...children);
    }

    if (!expandedNodes[_id] && children) {
      allExpanded = false;
    }

    item = queue.shift();
  }

  return (
    <div className="PaneTree">
      <div className="PaneTree-header">
        <Link to="/admin/teams">
          <span className="material-icons">people_outline</span>
          <span className="PaneTree-name">Teams</span>
        </Link>
      </div>
      <div className={classNames('PaneTree-content', 'dark-scroll')}>
        {orderedNodes.map((n: any) => {
          const expanded = expandedNodes[n._id];
          const hasChildren = !!n.children?.length;
          const childClassName = `is-child-${n.depth}`;
          return (
            <OverlayTrigger
              key={n._id}
              overlay={<Tooltip id={`tooltip-${n._id}`}>{n.name}</Tooltip>}
            >
              <div
                className={classNames('PaneTreeNode', {
                  active: teamId === n._id,
                })}
                onClick={() => {
                  navigate(`/admin/teams/${n._id}`);
                }}
              >
                <div
                  className={classNames('PaneTreeNode-spacing', {
                    [childClassName]: !!n.depth,
                  })}
                >
                  {hasChildren && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedNodes({ ...expandedNodes, [n._id]: !expanded });
                      }}
                      className={classNames('material-icons PaneTreeNode-icon')}
                    >
                      {expanded ? 'expand_more' : 'chevron_right'}
                    </span>
                  )}
                </div>
                <div className="PaneTreeNode-label">
                  <span className="PaneTreeNode-name">{n.name}</span>
                  <div
                    className="PaneTreeNode-options"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(`/admin/teams/new?parentId=${n._id}&parentName=${n.name}`);
                    }}
                  >
                    <span className="material-icons">add</span>
                  </div>
                </div>
              </div>
            </OverlayTrigger>
          );
        })}
      </div>
      <div className="PaneTree-footer">
        <Button
          className="PaneTree-add-team secondary"
          onClick={() => navigate(`/admin/teams/new`)}
        >
          + NEW TEAM
        </Button>
      </div>
    </div>
  );
};

export default TeamsTree;
